// src/components/login/admin/admin-brand-access-page.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// `Button`, `Banner` (62), `Modal` (56) via the shared `ConfirmDialog`, and
// `OverlayProvider` for the one scrim. `ui/button`, `ui/alert` and
// `ui/confirm-dialog` are gone. `data-kit` is on the root.
//
// What did NOT move: `brandsApi`, `authApi`, the three modes, the assignment
// model, the deletion-impact preview and every string.
//
// Two things the swap fixed:
//
//   * The success message was a bare coloured <span> with no role, so a
//     successful save was announced to nobody. It is a `Banner`, which has
//     one.
//   * The two destructive controls were secondary buttons repainted red with
//     inline `color` and `borderColor`. `Button` has a `destructive` variant,
//     so the look and the meaning are one prop.
import { useEffect, useMemo, useState, useCallback } from "react";
import { authApi, type UserMe } from "@/api/auth";
import { brandsApi, type AssignableBrand, type DeletionImpact } from "@/api/brands";
import {
  setCustomerKind,
  CUSTOMER_KINDS,
  CUSTOMER_KIND_LABEL,
  type CustomerKind,
} from "@/api/onboarding-api";
import { ConfirmDialog } from "@/app/components/shared/confirm-dialog";
import { DeletionImpactDescription } from "./deletion-impact-description";
import {
  ArrowLeft,
  Loader2,
  RefreshCcw,
  Search,
  X,
  Save,
  Building2,
  Check,
  ShieldCheck,
  User as UserIcon,
  Tag,
  Trash2,
  Database,
} from "lucide-react";
import { ModuleHeader } from "../../shell/module-header";
import { Banner, Button, OverlayProvider } from "../../kit";

type Mode = "list" | "assign" | "brands";

function isAdminFromStorage() {
  return (
    localStorage.getItem("is_admin") === "true" ||
    sessionStorage.getItem("is_admin") === "true"
  );
}

export function AdminBrandAccessPage() {
  const [mode, setMode] = useState<Mode>("list");

  const [users, setUsers] = useState<UserMe[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  // user_id -> assigned brand names (for the list cards)
  const [brandsByUser, setBrandsByUser] = useState<Record<number, string[]>>({});

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = useMemo(
    () => users.find((u) => u.id === selectedId) || null,
    [users, selectedId],
  );

  const [allBrands, setAllBrands] = useState<AssignableBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [loadingAssigned, setLoadingAssigned] = useState(false);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  // ── Brand kind (client / demo / prospect) ──
  // Which brand's kind is being written right now, so only that row's controls
  // go busy rather than the whole list.
  const [savingKindFor, setSavingKindFor] = useState<string | null>(null);

  const onChangeBrandKind = useCallback(
    async (brand: AssignableBrand, next: CustomerKind) => {
      if (!brand.customer_id || brand.kind === next || savingKindFor) return;
      setSavingKindFor(brand.brand_name);
      setErr("");
      setSuccess("");
      try {
        await setCustomerKind(brand.customer_id, next);
        // Patch the row in place. Re-fetching the whole list would also work
        // but would lose the admin's scroll position mid-way through marking
        // several brands.
        setAllBrands((prev) =>
          prev.map((b) =>
            b.brand_name === brand.brand_name ? { ...b, kind: next } : b,
          ),
        );
        setSuccess(
          next === "real" && brand.kind === "prospect"
            ? `${brand.brand_name} is now a client. Their next 10-repeat run becomes the baseline.`
            : `${brand.brand_name} is now marked ${CUSTOMER_KIND_LABEL[next].toLowerCase()}.`,
        );
      } catch (e: any) {
        // A 409 here means prospect reads are switched off for this
        // deployment; the backend's message says where to turn them on.
        setErr(String(e?.message || e));
      } finally {
        setSavingKindFor(null);
      }
    },
    [savingKindFor],
  );

  // ── Delete brand everywhere (hard delete + dry-run preview) ──
  const [brandDeleteOpen, setBrandDeleteOpen] = useState(false);
  const [deleteBrandTarget, setDeleteBrandTarget] = useState<string | null>(null);
  const [brandDeletePreview, setBrandDeletePreview] = useState<DeletionImpact | null>(null);
  const [previewingBrand, setPreviewingBrand] = useState<string | null>(null);
  const [deletingBrand, setDeletingBrand] = useState(false);

  // ── Load users ───────────────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setErr("");
    setSuccess("");
    setLoadingUsers(true);
    try {
      if (!isAdminFromStorage()) {
        setErr("Admin access required.");
        setUsers([]);
        return;
      }
      const list = await authApi.listUsers();
      const sorted = [...list].sort((a, b) =>
        (a.full_name || a.username).localeCompare(b.full_name || b.username),
      );
      setUsers(sorted);

      // Brand assignments per user — non-fatal if it fails (cards just
      // won't show chips until "Manage brands" is opened).
      try {
        const summary = await brandsApi.usersSummary();
        const map: Record<number, string[]> = {};
        summary.forEach((s) => { map[s.user_id] = s.brands; });
        setBrandsByUser(map);
      } catch {
        /* ignore — leave brandsByUser as-is */
      }
    } catch (e: any) {
      setErr(e?.message || "Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ── Load brand catalog ───────────────────────────────────────────────
  const loadBrands = useCallback(async (q?: string) => {
    setLoadingBrands(true);
    try {
      const list = await brandsApi.assignable(q);
      setAllBrands(list);
    } catch (e: any) {
      setErr(e?.message || "Failed to load brands");
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  // Debounced brand search (used by both the assign picker and the brand
  // data-management catalog).
  useEffect(() => {
    if (mode !== "assign" && mode !== "brands") return;
    const t = setTimeout(() => loadBrands(brandSearch || undefined), 250);
    return () => clearTimeout(t);
  }, [brandSearch, mode, loadBrands]);

  // ── Brand data management ────────────────────────────────────────────
  const openBrandManager = () => {
    setMode("brands");
    setErr("");
    setSuccess("");
    setBrandSearch("");
    loadBrands(); // immediate; debounce effect also keeps it fresh on search
  };

  const onAskDeleteBrand = async (brand: string) => {
    setErr("");
    setSuccess("");
    setPreviewingBrand(brand);
    try {
      const preview = await brandsApi.previewDeleteBrand(brand);
      setDeleteBrandTarget(brand);
      setBrandDeletePreview(preview);
      setBrandDeleteOpen(true);
    } catch (e: any) {
      setErr(e?.message || "Failed to preview brand deletion");
    } finally {
      setPreviewingBrand(null);
    }
  };

  const onConfirmDeleteBrand = async () => {
    if (!deleteBrandTarget) return;
    const brand = deleteBrandTarget;
    setBrandDeleteOpen(false);
    setErr("");
    setSuccess("");
    setDeletingBrand(true);
    try {
      const res = await brandsApi.deleteBrand(brand);
      const tables = Object.keys(res.impact).length;
      setSuccess(
        `Brand "${brand}" deleted — ${res.total_rows.toLocaleString()} row${
          res.total_rows === 1 ? "" : "s"
        } removed across ${tables} table${tables === 1 ? "" : "s"}.`,
      );
      // Refresh the catalog and user/brand assignments (deleting a brand also
      // removes its user_brands rows).
      await Promise.all([loadBrands(brandSearch || undefined), loadUsers()]);
    } catch (e: any) {
      setErr(e?.message || "Failed to delete brand");
    } finally {
      setDeletingBrand(false);
      setDeleteBrandTarget(null);
      setBrandDeletePreview(null);
    }
  };

  const openAssign = async (user: UserMe) => {
    setSelectedId(user.id);
    setMode("assign");
    setErr("");
    setSuccess("");
    setBrandSearch("");
    setLoadingAssigned(true);
    try {
      const [catalog, current] = await Promise.all([
        brandsApi.assignable(),
        brandsApi.getUserBrands(user.id),
      ]);
      setAllBrands(catalog);
      setAssigned(new Set(current.brands));
    } catch (e: any) {
      setErr(e?.message || "Failed to load brand assignments");
    } finally {
      setLoadingAssigned(false);
    }
  };

  const backToList = () => {
    setMode("list");
    setSelectedId(null);
    setErr("");
    setSuccess("");
  };

  const toggleBrand = (brand: string) => {
    setAssigned((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  };

  const onSave = async () => {
    if (!selected) return;
    setSaving(true);
    setErr("");
    setSuccess("");
    try {
      const res = await brandsApi.setUserBrands(
        selected.id,
        Array.from(assigned),
      );
      setAssigned(new Set(res.brands));
      setBrandsByUser((prev) => ({ ...prev, [selected.id]: res.brands }));
      setSuccess(
        `Saved. ${res.brands.length} brand${
          res.brands.length === 1 ? "" : "s"
        } assigned to ${selected.full_name || selected.username}.`,
      );
    } catch (e: any) {
      setErr(e?.message || "Failed to save brand assignments");
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      `${u.full_name || ""} ${u.username || ""}`.toLowerCase().includes(q),
    );
  }, [users, userSearch]);

  // Merge any assigned brand that isn't in the current catalog page so
  // selections never silently disappear behind a search filter.
  const visibleBrands = useMemo(() => {
    const names = new Set(allBrands.map((b) => b.brand_name));
    const extras: AssignableBrand[] = Array.from(assigned)
      .filter((b) => !names.has(b))
      .map((b) => ({ brand_name: b, source: "assigned" }));
    return [...allBrands, ...extras];
  }, [allBrands, assigned]);

  return (
    <OverlayProvider className="upm-page" contained>
      <div data-kit>
    <ModuleHeader
      page="Brand Access"
      icon={Tag}
      compact
      title="Brand access"
      description="Admin-only. Assign saved brands to a user. They'll only be able to load and switch between the brands you assign here."
      actionsSlot={
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto flex-shrink-0">
          {mode === "list" && (
            <Button
              variant="destructive"
              icon={Database}
              onClick={openBrandManager}
              title="Delete a brand and all its data across the platform"
            >
              Manage brand data
            </Button>
          )}
          <Button
            variant="secondary"
            icon={RefreshCcw}
            onClick={loadUsers}
            disabled={loadingUsers}
            loading={loadingUsers}
            loadingLabel="Refreshing"
          >
            Refresh
          </Button>
        </div>
      }
    />
    <div className="px-4 md:px-6 py-5 md:py-6">
      {err ? <Banner tone="error">{err}</Banner> : null}
      {success ? <Banner tone="ok">{success}</Banner> : null}

      {/* ── LIST MODE: pick a user ───────────────────────────────────── */}
      {mode === "list" && (
        <>
          <div
            className="mt-4 p-3 md:p-4 rounded-xl"
            style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
            }}
          >
            <div className="relative">
              <Search
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 15,
                  height: 15,
                  color: "var(--muted-foreground)",
                  pointerEvents: "none",
                }}
              />
              <input
                type="search"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search a user by name or email…"
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 36px",
                  borderRadius: 9,
                  border: "1px solid var(--border)",
                  backgroundColor: "var(--input-background)",
                  fontSize: 13,
                  outline: "none",
                  boxSizing: "border-box",
                  color: "var(--foreground)",
                }}
              />
              {userSearch && (
                <button
                  onClick={() => setUserSearch("")}
                  aria-label="Clear search"
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: 4,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--muted-foreground)",
                    display: "flex",
                  }}
                >
                  <X style={{ width: 14, height: 14 }} />
                </button>
              )}
            </div>
            {!loadingUsers && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: "var(--muted-foreground)",
                }}
              >
                Showing{" "}
                <strong style={{ color: "var(--foreground)" }}>
                  {filteredUsers.length}
                </strong>{" "}
                of {users.length} {users.length === 1 ? "user" : "users"}
              </div>
            )}
          </div>

          {loadingUsers ? (
            <div
              className="mt-4 p-5 flex items-center justify-center gap-3"
              style={{
                backgroundColor: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                color: "var(--muted-foreground)",
              }}
            >
              <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 13 }}>Loading users…</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div
              className="mt-4 p-8 text-center"
              style={{
                backgroundColor: "var(--card)",
                border: "1px dashed var(--border)",
                borderRadius: 12,
                color: "var(--muted-foreground)",
              }}
            >
              <UserIcon
                style={{ width: 36, height: 36, margin: "0 auto 10px", opacity: 0.4 }}
              />
              <div style={{ fontSize: 14, fontWeight: 600 }}>No users found</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 mt-4">
              {filteredUsers.map((u) => (
                <div
                  key={u.id}
                  className="flex flex-col gap-3 p-4 rounded-xl border"
                  style={{
                    backgroundColor: "var(--card)",
                    borderColor: "var(--border)",
                  }}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className="flex-shrink-0 flex items-center justify-center"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: u.is_admin
                          ? "var(--alpha-teal-10)"
                          : "var(--muted)",
                        border: `1px solid ${
                          u.is_admin ? "var(--alpha-teal-20)" : "var(--border)"
                        }`,
                      }}
                    >
                      {u.is_admin ? (
                        <ShieldCheck
                          style={{ width: 18, height: 18, color: "var(--alpha-teal)" }}
                        />
                      ) : (
                        <UserIcon
                          style={{
                            width: 18,
                            height: 18,
                            color: "var(--muted-foreground)",
                          }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        style={{
                          fontWeight: 800,
                          color: "var(--foreground)",
                          fontSize: 14,
                          lineHeight: 1.2,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {/* An em dash for a missing name breaks two rules at
                            once: no em dash in user-facing text, and never a
                            dash for an absence. The username is always present
                            and is what an admin searches by. */}
                        {u.full_name || u.username}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--muted-foreground)",
                          marginTop: 3,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {u.username}
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const ub = brandsByUser[u.id] || [];
                    const MAX = 6;
                    const shown = ub.slice(0, MAX);
                    const extra = ub.length - shown.length;
                    return (
                      <div
                        style={{
                          paddingTop: 10,
                          borderTop: "1px solid var(--border)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "var(--muted-foreground)",
                            letterSpacing: "0.04em",
                            marginBottom: ub.length ? 8 : 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Building2
                            style={{ width: 13, height: 13, color: "var(--alpha-teal)" }}
                          />
                          {/* The count is dropped when there is nothing to
                              count: "(0)" beside "None yet" is a zero printed
                              for an absence AND the same fact stated twice. */}
                          ASSOCIATED BRANDS{ub.length ? ` (${ub.length})` : ""}
                        </div>

                        {ub.length === 0 ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--muted-foreground)",
                            }}
                          >
                            None yet
                          </div>
                        ) : (
                          <div
                            style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
                          >
                            {shown.map((b) => (
                              <span
                                key={b}
                                title={b}
                                style={{
                                  display: "inline-block",
                                  maxWidth: 150,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  padding: "3px 9px",
                                  borderRadius: 999,
                                  backgroundColor: "var(--alpha-teal-10)",
                                  border: "1px solid var(--alpha-teal-20)",
                                  color: "var(--alpha-teal)",
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                {b}
                              </span>
                            ))}
                            {extra > 0 && (
                              <span
                                style={{
                                  padding: "3px 9px",
                                  borderRadius: 999,
                                  backgroundColor: "var(--muted)",
                                  border: "1px solid var(--border)",
                                  color: "var(--muted-foreground)",
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                +{extra} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="pt-2 mt-auto" style={{ borderTop: "1px solid var(--border)" }}>
                    <Button variant="secondary" icon={Tag} onClick={() => openAssign(u)}>
                      Manage brands
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── ASSIGN MODE: pick brands for the user ────────────────────── */}
      {mode === "assign" && selected && (
        <div
          className="p-4 md:p-5"
          style={{
            marginTop: 16,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
          }}
        >
          <button
            onClick={backToList}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--signal-blue)",
              fontWeight: 700,
              padding: 0,
              marginBottom: 12,
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            Back to users
          </button>

          <div
            style={{
              fontWeight: 800,
              fontSize: 16,
              marginBottom: 4,
              color: "var(--foreground)",
            }}
          >
            Brands for{" "}
            <span style={{ color: "var(--signal-blue)" }}>
              {selected.full_name || selected.username}
            </span>
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--muted-foreground)",
              marginBottom: 14,
            }}
          >
            {selected.username} ·{" "}
            <strong style={{ color: "var(--foreground)" }}>
              {assigned.size}
            </strong>{" "}
            selected
          </div>

          {/* Currently associated — shown first, before the catalog */}
          <div
            style={{
              marginBottom: 16,
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--foreground)",
                marginBottom: assigned.size ? 10 : 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Building2 style={{ width: 14, height: 14, color: "var(--alpha-teal)" }} />
              Associated brands ({assigned.size})
            </div>

            {assigned.size === 0 ? (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--muted-foreground)",
                }}
              >
                No brands associated yet. Pick from the list below to associate
                brands with this user.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {Array.from(assigned)
                  .sort((a, b) => a.localeCompare(b))
                  .map((b) => (
                    <span
                      key={b}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "5px 8px 5px 10px",
                        borderRadius: 999,
                        backgroundColor: "var(--alpha-teal-10)",
                        border: "1px solid var(--alpha-teal-20)",
                        color: "var(--alpha-teal)",
                        fontSize: 12,
                        fontWeight: 600,
                        maxWidth: "100%",
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 180,
                        }}
                      >
                        {b}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleBrand(b)}
                        aria-label={`Remove ${b}`}
                        title={`Remove ${b}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "var(--alpha-teal)",
                          padding: 0,
                        }}
                      >
                        <X style={{ width: 13, height: 13 }} />
                      </button>
                    </span>
                  ))}
              </div>
            )}
          </div>

          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--foreground)",
              marginBottom: 8,
            }}
          >
            Manage brands
          </div>

          {/* Brand search */}
          <div className="relative" style={{ marginBottom: 12 }}>
            <Search
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                width: 15,
                height: 15,
                color: "var(--muted-foreground)",
                pointerEvents: "none",
              }}
            />
            <input
              type="search"
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder="Search saved brands…"
              style={{
                width: "100%",
                padding: "10px 12px 10px 36px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                backgroundColor: "var(--input-background)",
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
                color: "var(--foreground)",
              }}
            />
          </div>

          {loadingAssigned || loadingBrands ? (
            <div
              className="p-5 flex items-center justify-center gap-3"
              style={{ color: "var(--muted-foreground)" }}
            >
              <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 13 }}>Loading brands…</span>
            </div>
          ) : visibleBrands.length === 0 ? (
            <div
              className="p-8 text-center"
              style={{
                border: "1px dashed var(--border)",
                borderRadius: 12,
                color: "var(--muted-foreground)",
              }}
            >
              <Building2
                style={{ width: 32, height: 32, margin: "0 auto 8px", opacity: 0.4 }}
              />
              <div style={{ fontSize: 13 }}>
                No saved brands found. Brands appear here once they exist in
                onboarding or brand monitoring.
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                maxHeight: 420,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {visibleBrands.map((b) => {
                const isOn = assigned.has(b.brand_name);
                return (
                  <button
                    key={b.brand_name}
                    onClick={() => toggleBrand(b.brand_name)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "11px 14px",
                      borderRadius: 10,
                      cursor: "pointer",
                      textAlign: "left",
                      border: `1px solid ${
                        isOn ? "var(--alpha-teal)" : "var(--border)"
                      }`,
                      backgroundColor: isOn
                        ? "var(--alpha-teal-10)"
                        : "var(--muted)",
                      transition: "all 0.15s",
                    }}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1px solid ${
                          isOn ? "var(--alpha-teal)" : "var(--border)"
                        }`,
                        backgroundColor: isOn ? "var(--alpha-teal)" : "transparent",
                      }}
                    >
                      {isOn && (
                        <Check style={{ width: 14, height: 14, color: "white" }} />
                      )}
                    </div>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: isOn ? 600 : 500,
                        color: isOn ? "var(--alpha-teal)" : "var(--foreground)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {b.brand_name}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--muted-foreground)",
                        textTransform: "capitalize",
                        flexShrink: 0,
                      }}
                    >
                      {b.source}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-5">
            <Button
              icon={Save}
              onClick={onSave}
              disabled={saving || loadingAssigned}
              loading={saving}
              loadingLabel="Saving"
            >
              Save assignments
            </Button>
            <Button variant="secondary" onClick={backToList} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ── BRANDS MODE: delete a brand and all its data ─────────────── */}
      {mode === "brands" && (
        <div
          className="p-4 md:p-5"
          style={{
            marginTop: 16,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
          }}
        >
          <button
            onClick={() => {
              setMode("list");
              setErr("");
              setSuccess("");
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--signal-blue)",
              fontWeight: 700,
              padding: 0,
              marginBottom: 12,
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            Back to users
          </button>

          <div
            style={{
              fontWeight: 800,
              fontSize: 16,
              marginBottom: 4,
              color: "var(--foreground)",
            }}
          >
            Manage brand data
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--muted-foreground)",
              marginBottom: 14,
              lineHeight: 1.5,
            }}
          >
            Mark what each brand is for, or permanently delete a brand and{" "}
            <strong>all</strong> of its data across every table (monitoring
            runs, signals, fix actions, publishing, agents, onboarding, API
            keys, assignments, …). You'll see exactly how many rows will be
            removed before confirming. Deletion cannot be undone.
          </div>

          <div
            style={{
              fontSize: 12,
              color: "var(--muted-foreground)",
              marginBottom: 14,
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: "var(--foreground)" }}>Client</strong> is a
            paying customer: reads use the full 10 repeats and the first one is
            the baseline.{" "}
            <strong style={{ color: "var(--foreground)" }}>Demo</strong> and{" "}
            <strong style={{ color: "var(--foreground)" }}>Prospect</strong>{" "}
            brands are visible to <em>every</em> login here. A prospect is read
            at 3 repeats and is never the baseline — mark them a client when
            they sign, and their next 10-repeat run becomes the baseline while
            the prospect read stays in history. You can also set this at Stage 1
            when a brand is first created.
          </div>

          {/* Brand search */}
          <div className="relative" style={{ marginBottom: 12 }}>
            <Search
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                width: 15,
                height: 15,
                color: "var(--muted-foreground)",
                pointerEvents: "none",
              }}
            />
            <input
              type="search"
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder="Search saved brands…"
              style={{
                width: "100%",
                padding: "10px 12px 10px 36px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                backgroundColor: "var(--input-background)",
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
                color: "var(--foreground)",
              }}
            />
          </div>

          {loadingBrands ? (
            <div
              className="p-5 flex items-center justify-center gap-3"
              style={{ color: "var(--muted-foreground)" }}
            >
              <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 13 }}>Loading brands…</span>
            </div>
          ) : allBrands.length === 0 ? (
            <div
              className="p-8 text-center"
              style={{
                border: "1px dashed var(--border)",
                borderRadius: 12,
                color: "var(--muted-foreground)",
              }}
            >
              <Building2
                style={{ width: 32, height: 32, margin: "0 auto 8px", opacity: 0.4 }}
              />
              <div style={{ fontSize: 13 }}>No saved brands found.</div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                maxHeight: 460,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {allBrands.map((b) => {
                const busy = previewingBrand === b.brand_name;
                return (
                  <div
                    key={b.brand_name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "11px 14px",
                      borderRadius: 10,
                      border: "1px solid var(--border)",
                      backgroundColor: "var(--muted)",
                    }}
                  >
                    <Building2
                      style={{
                        width: 18,
                        height: 18,
                        flexShrink: 0,
                        color: "var(--muted-foreground)",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--foreground)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {b.brand_name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--muted-foreground)",
                          textTransform: "capitalize",
                          marginTop: 2,
                        }}
                      >
                        {b.source}
                        {b.last_activity
                          ? ` · last activity ${new Date(
                              b.last_activity,
                            ).toLocaleDateString()}`
                          : ""}
                      </div>
                    </div>
                    {/* What this brand is for. Only an onboarded brand has a
                        customer row to carry `kind`, so a monitoring-only
                        brand says so instead of showing a dead control. */}
                    {b.customer_id ? (
                      <div
                        style={{ display: "flex", gap: 4, flexShrink: 0 }}
                        role="group"
                        aria-label={`What ${b.brand_name} is for`}
                      >
                        {CUSTOMER_KINDS.map((option) => {
                          const isCurrent = (b.kind ?? "real") === option;
                          const rowSaving = savingKindFor === b.brand_name;
                          return (
                            <button
                              key={option}
                              type="button"
                              onClick={() => onChangeBrandKind(b, option)}
                              disabled={rowSaving || !!savingKindFor || deletingBrand}
                              aria-pressed={isCurrent}
                              title={
                                option === "prospect"
                                  ? "Read at 3 repeats before they sign. Never the baseline."
                                  : option === "demo"
                                    ? "A standing demo brand, visible to every login."
                                    : "A paying client: 10 repeats, first run is the baseline."
                              }
                              style={{
                                padding: "5px 9px",
                                borderRadius: 7,
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: savingKindFor ? "default" : "pointer",
                                border: `1px solid ${isCurrent ? "var(--alpha-teal)" : "var(--border)"}`,
                                backgroundColor: isCurrent
                                  ? "var(--alpha-teal)"
                                  : "var(--card, transparent)",
                                color: isCurrent ? "#fff" : "var(--muted-foreground)",
                                opacity: rowSaving ? 0.6 : 1,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {CUSTOMER_KIND_LABEL[option]}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--muted-foreground)",
                          flexShrink: 0,
                          maxWidth: 160,
                          lineHeight: 1.4,
                        }}
                        title="Only an onboarded brand can be marked demo or prospect."
                      >
                        Not onboarded — no kind to set
                      </div>
                    )}

                    <Button
                      type="button"
                      variant="destructive"
                      icon={Trash2}
                      onClick={() => onAskDeleteBrand(b.brand_name)}
                      disabled={busy || deletingBrand}
                      loading={busy}
                      loadingLabel="Deleting"
                      title={`Delete ${b.brand_name} everywhere`}
                    >
                      Delete
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={brandDeleteOpen}
        onOpenChange={(o) => {
          setBrandDeleteOpen(o);
          if (!o) {
            setDeleteBrandTarget(null);
            setBrandDeletePreview(null);
          }
        }}
        onConfirm={onConfirmDeleteBrand}
        destructive
        title={`Delete brand "${deleteBrandTarget ?? ""}"?`}
        confirmLabel="Delete permanently"
        description={<DeletionImpactDescription preview={brandDeletePreview} noun="brand" />}
      />
    </div>
      </div>
    </OverlayProvider>
  );
}